import { useState, useEffect, createContext, useContext } from "react";
import { useAuth } from "@/lib/auth";

export type CardDensity = "compact" | "normal" | "comfortable";

const DEFAULT: CardDensity = "normal";

function storageKey(userId: string | undefined): string {
  return userId ? `vri-density-${userId}` : "vri-density";
}

const VALID_DENSITIES: CardDensity[] = ["compact", "normal", "comfortable"];
function isValidDensity(v: string | null): v is CardDensity {
  return v !== null && (VALID_DENSITIES as string[]).includes(v);
}

function readDensity(userId: string | undefined): CardDensity {
  try {
    const stored = localStorage.getItem(storageKey(userId));
    return isValidDensity(stored) ? stored : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

/**
 * Per-user card density preference, stored in localStorage.
 * compact:     tighter rows, smaller images — fit more on screen
 * normal:      balanced (default)
 * comfortable: roomier rows, larger images — easier to tap
 *
 * Future: add `card_density` column to user_preferences table and sync here
 * for cross-device persistence. Migration: see supabase/migrations for the pending column.
 */
export function useCardDensity(): [CardDensity, (d: CardDensity) => void] {
  const { user } = useAuth();

  const [density, setDensityState] = useState<CardDensity>(() => readDensity(undefined));

  // Re-read once the user ID is known so we get the user-specific preference
  useEffect(() => {
    if (!user?.id) return;
    setDensityState(readDensity(user.id));
  }, [user?.id]);

  function setDensity(d: CardDensity) {
    setDensityState(d);
    try {
      localStorage.setItem(storageKey(user?.id), d);
    } catch {
      // localStorage unavailable (private browsing, storage full) — silently ignore
    }
  }

  return [density, setDensity];
}

/**
 * Shared density context — provided once by DashboardLayout.
 * All pages read from this so changing density on any page updates all pages instantly.
 */
export const DensityContext = createContext<[CardDensity, (d: CardDensity) => void]>(["normal", () => {}]);

export function useDensity(): [CardDensity, (d: CardDensity) => void] {
  return useContext(DensityContext);
}

/** Image size in px for each density level */
export const DENSITY_IMAGE_SIZE: Record<CardDensity, number> = {
  compact: 36,
  normal: 52,
  comfortable: 96,
};

/** Tailwind padding classes for each density level */
export const DENSITY_PADDING: Record<CardDensity, string> = {
  compact: "px-3 py-2",
  normal: "px-3 py-3",
  comfortable: "px-4 py-5",
};

/** Tailwind text size classes for each density level (card view) */
export const DENSITY_TEXT: Record<CardDensity, { name: string; meta: string; qty: string }> = {
  compact: { name: "text-sm", meta: "text-xs", qty: "text-sm" },
  normal: { name: "text-sm", meta: "text-xs", qty: "text-base" },
  comfortable: { name: "text-base font-semibold", meta: "text-sm", qty: "text-lg" },
};

/** Table view density — controls cell padding, text size, and image size */
export const DENSITY_TABLE: Record<CardDensity, {
  imgSize: number;
  name: string;
  meta: string;
  price: string;
  cellPy: string;
  headerPy: string;
}> = {
  compact:     { imgSize: 36, name: "text-sm font-medium",     meta: "text-xs", price: "text-sm", cellPy: "[&_td]:py-2",  headerPy: "[&_th]:py-2" },
  normal:      { imgSize: 52, name: "text-sm font-medium",     meta: "text-xs", price: "text-sm", cellPy: "[&_td]:py-3",  headerPy: "[&_th]:py-3" },
  comfortable: { imgSize: 96, name: "text-base font-semibold", meta: "text-sm", price: "text-sm", cellPy: "[&_td]:py-5",  headerPy: "[&_th]:py-4" },
};
